import angular from "angular";

import { Attachment, Records, Record, Translations, WorkflowEvent, RecordListItem } from "@salaxy/core";

import { UiHelpers, UploadService, WorkflowService } from "../../services";

import { ApiCrudObjectController } from "../bases/ApiCrudObjectController";


/**
 * Base controller for records management.
 */
export class RecordsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Records",
    "UiHelpers",
    "UploadService",
    "WorkflowService"
  ];

  /** Current records. */
  public records: Record[];

  /** Readonly binding */
  public readOnly: boolean;

  /** API CRUD controller for the workflow object.*/
  public apiCtrl: ApiCrudObjectController<Record, RecordListItem>;

  /** Entity type */
  public entityType: string;

  /** Loding indicator */
  public isLoading: boolean;

  private _wfType = "EmployerMessageRecord";
  private _events: WorkflowEvent[] = [];

  constructor(
    protected api: Records,
    protected uiHelpers: UiHelpers,
    protected uploadService: UploadService,
    protected workflowService: WorkflowService,
  ) { }

  /**
   * Loads the records for the entity.
   */
  public $onInit() {
    this.reload();
  }

  /**
   * Reloads data
   */
  public reload() {
    this.isLoading = true;
    this.api.getForEntity(this.entityType, this.entityId).then((data) => {
      this.records = data;
      this.isLoading = false;
    });
  }

  /** Returns events for the record. */
  public getEvents(item: Record) {
    return this.events.filter((x) => x.record && x.record === item.id);
  }

  /** Returns single event for the record. */
  public getSingleEvent(item: Record): WorkflowEvent {
    const wfEvents = this.getEvents(item);
    return wfEvents.length > 0 ? wfEvents[0] : {};
  }

  /**
   * Shows the edit dialog.
   * @param item Item to edit or to create.
   * @param index Current index. -1 for new.
   */
  public showEditRecord(item: Record, index = -1) {
    const logic = {
      title: index < 0 ? Translations.get("SALAXY.UI_Terms.add") : this.readOnly ? Translations.get("SALAXY.UI_Terms.show") : Translations.get("SALAXY.UI_Terms.edit"),
      showDelete: index > -1 && !this.readOnly,
      readOnly: this.readOnly,
      recordTypes: [],
      getAttachmentDownloadUrl: (attachment) => this.getAttachmentDownloadUrl(attachment),
      getAttachmentIcon: (attachment) => this.getAttachmentIcon(attachment),
      uploadAttachments: (files, attachments) => this.uploadAttachments(files, attachments),
    };

    item.entityType = this.entityType;
    item.entityId = this.entityId;
    const recordAndEvent = { record: item, wfEvent: this.getSingleEvent(item)};

    logic.recordTypes.push(... this.records.map(x => x.recordType).filter((v, i, a) => a.indexOf(v) === i));
    logic.recordTypes.sort();
    this.uiHelpers.openEditDialog("salaxy-components/modals/records/Record.html", recordAndEvent, logic,"lg").then((result) => {
      if (result.action === "ok") {
        if (index === -1) {
          // Add
          this.api.save(result.item.record).then((data) => {
            this.records.push(data);
            this.saveWorkflowEvent(data, result.item.wfEvent);
          });
        } else {
          // Modify
          this.api.save(result.item.record).then((data) => {
            angular.copy(data, this.records[index]);
            this.saveWorkflowEvent(this.records[index], result.item.wfEvent);
          });
        }
      } else if (result.action === "delete") {
        this.deleteRecord(result.item.record, index);
        this.deleteWorkflowEvent(result.item.wfEvent);
      }
    });
    this.api.getRecordTypes().then((serverRecordTypes) => {
      logic.recordTypes.push(... (serverRecordTypes ?? []));
      logic.recordTypes.splice(0, logic.recordTypes.length, ...logic.recordTypes.filter((v, i, a) => a.indexOf(v) === i));
      logic.recordTypes.sort();
    });
  }

  /**
   * Deletes one record
   * @param item Item to delete.
   * @param index Current index.
   */
  public deleteRecord(item: Record, index = -1) {
    if (index > -1) {
      // Delete
      const wfItem = this.getSingleEvent(item);
      return this.api.delete(item.id).then(() => {
        this.records.splice(index, 1);
        return this.deleteWorkflowEvent(wfItem);
      });
    }
  }

  /** Returns file icon for the attachment. */
  public getAttachmentIcon(attachment: Attachment): string {
    return this.uploadService.getFileIcon(attachment?.fileName);
  }

  /** Returns download url for the attachment. */
  public getAttachmentDownloadUrl(attachment: Attachment): string {
    return this.uploadService.getFileUrl(attachment?.contentId);
  }

  /** Returns the upload url for the attachment */
  public getAttachmentUploadUrl(): string {
    return this.api.getRecordUploadUrl(this.entityType, this.entityId);
  }

  /**
   * Uploads the attachments to the server, and adds them to the attchment collection.
   * @param files The files collection from the upload component
   * @param attachments The attachment collection for the results.
   */
  private uploadAttachments(files: any[], attachments: Attachment[]): any {
    if (files && files.length) {
      const uploadUrl = this.getAttachmentUploadUrl();
      const loader = this.uiHelpers.showLoading("SALAXY.NG1.MessageThreadComponent.uploadFilesSaving");
      return this.uploadService.upload<Attachment[]>(
        uploadUrl, { files }).then((data) => {
          if (data) {
            attachments.push(...data);
          }
          loader.dismiss();
          return data;
        }).catch((response) => {
          loader.dismiss();
          this.uiHelpers.showAlert("SALAXY.UI_Terms.error", "SALAXY.NG1.MessageThreadComponent.uploadFilesError");
          throw new Error(response);
        });
    }
  }

  private deleteWorkflowEvent(wfEvent: WorkflowEvent): Promise<any> {
    return this.cleanWorkflowEvents().then( () => {
      if (wfEvent.id) {
        return this.workflowService.deleteWorkflowEvent(this.apiCtrl, wfEvent.id);
      }
      return Promise.resolve(this.apiCtrl.current);
    });
  }

  private saveWorkflowEvent(item: Record, wfEvent: WorkflowEvent): Promise<any> {
    if (wfEvent.message) {
      return this.cleanWorkflowEvents().then( () => {
        // add or update
        wfEvent.record = item.id;
        wfEvent.type = this._wfType;
        return this.workflowService.saveWorkflowEvent(this.apiCtrl, wfEvent);
      });
    }
    else
    {
      // delete
      return this.deleteWorkflowEvent(wfEvent);
    }
  }

  private async cleanWorkflowEvents():Promise<any> {
    // All events with no record, or with record and no matching record.id
    const orphans = this.events.filter( (x) => x.id && (!x.record || (x.record && !this.records.some( (r) => x.record === r.id))));
    // delete all orphans in sequence, otherwise the events are not deleted correctly
    for(const orphan of orphans) {
      await this.workflowService.deleteWorkflowEvent(this.apiCtrl, orphan.id);
    }
    return this.apiCtrl.current;
  }

  private get entityId(): string {
    return this.entityType == "employment" ?
     // Support old worker based cruds (not sure if this hack is needed anymore)
    (this.apiCtrl.getApi().getBaseUrl() === "/accounts/workers" ? (this.apiCtrl.current as any).employmentId : this.apiCtrl.current.id) :
    this.entityType == "account" ? null : this.apiCtrl.current.id;
  }

  private get events(): WorkflowEvent[] {
    const eventsOfType = ((this.apiCtrl.current as any)?.workflowData?.events ?? []).filter((x) => x.type == this._wfType);
    this.modifyArray(eventsOfType, this._events);
    return this._events;
  }

  private modifyArray(
    source: any[],
    target: any[]) {
    if (angular.equals(source, target)) {
      return;
    }
    target.splice(0, target.length);
    target.push(...source);
  }

}
